import {Divider, SnackbarService, withMobile} from "@hps/hops-react";
import {CheckoutBasketItem, Localisation, OrderableTypes, TicketSeatReservationModes} from "@hps/hops-sdk-js";
import Badge from "@mui/material/Badge";
import pluralize from "pluralize";
import React from "react";

import Button from "Components/Button.js";
import ButtonAddProductToBasket from "Components/ButtonAddProductToBasket";
import Container from "Components/Container.js";
import Flex from "Components/Flex.js";
import Hidden from "Components/Hidden";
// import SeatReservationChip from "Components/SeatReservationChip";
import String from "Components/String.js";
import dSeatReservationDialog from "Dispatchers/dSeatReservationDialog.js";
import withCheckoutBasket from "Hoc/withCheckoutBasket";
import AsyncStatefulComponent from "Includes/AsyncStatefulComponent.js";
import CheckoutService from "Services/CheckoutService.js";

import DeliveryMethodUtils from "./DeliveryMethodUtils";

import SeatingIcon from "@mui/icons-material/EventSeatOutlined";
import ShieldIcon from "@mui/icons-material/Shield";

import scss from "./BasketCardItem.module.scss";


/**
 * Basket card item
 *
 * Renders an individual item in `BasketCard`.
 * 
 * @package HOPS
 * @subpackage Checkout
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class BasketCardItem extends AsyncStatefulComponent {

	/**
	 * Remove the item from the basket.
	 * 
	 * @return {void}
	 */
	handleRemove = async () => {

		if (this.props.onRemoving) {
			this.props.onRemoving(true);
		}

		try {
			await CheckoutService.removeBasketItems([this.props.item]);
		}
		catch (e) {
			SnackbarService.snack.snack(e);
		}

		if (this.props.onRemoving) {
			this.props.onRemoving(false);
		}

	};


	/**
	 * Seat reservation button handler.
	 *
	 * @return {void}
	 */
	handleSeatReservation = () => {
		dSeatReservationDialog(this.props.item.Uuid);
	};


	/**
	 * Render.
	 * 
	 * @return {ReactNode}
	 */
	render() {

		const {Price, Quantity} = this.props.item;
		let label = CheckoutBasketItem.getUiLabel(this.props.item);

		if (Quantity > 1) {
			label += ` (@ ${Localisation.formatCurrency(Price)})`;
		}

		const seatReservationMode = CheckoutBasketItem.getSeatReservationMode(this.props.item);
		const supportsSeatReservation = TicketSeatReservationModes.allowsCustomerSelection(seatReservationMode);

		return (
			<Flex>
				<Divider />
				<Flex gap={0.25}>
					<Container
						columns="auto max-content max-content">

						{/**
						 * CB 12/08/2026
						 * DO NOT enable the bin icon on pain of death and evisceration
						 * The client does not handle deleting related items correctly!!
						 */}

						{/* (canModify &&
							<Hidden hidden={hideDelete}>
								<Flex alignItems="flex-end">
									<Button
										color="secondary"
										disabled={this.props.disabled}
										icon={DeleteIcon}
										label="Remove item"
										onClick={this.handleRemove}
										size="small" />
								</Flex>
							</Hidden>
						)*/}
						<Flex
							gap={0.375}>
							<Flex
								columnar={true}
								justifyContent="space-between">

								<String
									noFlex={true}
									str={label} />
							</Flex>

						</Flex>
						<Flex
							alignItems="flex-end"
							gap={0}>
							{(this.props.disableModification || this.props.item.OrderableType !== OrderableTypes.RailwayProduct) ?
								<Hidden hidden={[OrderableTypes.TicketProtectionInstance, OrderableTypes.DeliveryMethod].includes(this.props.item.OrderableType)}>
									<String
										color="textSecondary"
										noFlex={true}
										str={`x${Quantity}`} />
								</Hidden> :
								<ButtonAddProductToBasket
									item={this.props.item.Item}
									disabled={this.props.disableModification}
									disablePriceInput={true} />}
						</Flex>
						<Flex alignItems="flex-end">
							<String
								className={scss.price}
								noFlex={true}
								str={Localisation.formatCurrency((Price * Quantity))} />
						</Flex>
					</Container>
					{this.renderTicketProtection()}
					{this.renderDiscounts()}
					{this.renderDetails()}
					{this.renderDeliveryMethod()}
					{(!this.props.disableModification && supportsSeatReservation && this.renderSeatReservation())}
					{(this.props.disableModification && this.renderSeatReservationView())}
				</Flex>
			</Flex>
		);

	}


	/**
	 * Render the details section.
	 * 
	 * @return {ReactNode}
	 */
	renderDetails() {

		const details = CheckoutBasketItem.getUiDetails(this.props.item);


		if (details?.length) {
			return (
				<details>
					<summary className={scss.summary} title="View Details">
						{this.renderDetailsHeader()}
					</summary>
					<Flex gap={0.25} pl={0.75} mt={0.25}>
						{
							details.map((detail, key) => (
								<div key={key}>
									<String
										display="inline-block"
										noFlex={true}
										str={`${detail.label}${(detail.value ? ": " : "")}`}
										variant="body2"
										whiteSpace="pre-wrap" />
									<String
										color="textSecondary"
										display="inline-block"
										noFlex={true}
										str={detail.value}
										variant="body2" />
								</div>
							))
						}
					</Flex>
				</details>
			);
		}
		else return this.renderDetailsHeader();

	}


	/**
	 * Render the details section's header.
	 *
	 * @return {ReactNode}
	 */
	renderDetailsHeader() {
		return (
			<String
				color="textSecondary"
				display="inline-block"
				noFlex={true}
				str={CheckoutBasketItem.getUiSubtext(this.props.item)}
				variant="body2" />
		);
	}


	/**
	 * Render the delivery method information.
	 *
	 * @return {ReactNode}
	 */
	renderTicketProtection() {
		if (this.props.item.TicketProtectionItemUuid) {
			const ticketProtectionItem = this.props.checkoutBasket.find(i => i.Uuid === this.props.item.TicketProtectionItemUuid);

			return (
				<Flex alignItems="center" columnar={true} gap={0.25}>
					<ShieldIcon color="info" fontSize="small" />
					<String
						color="textSecondary"
						display="inline-block"
						noFlex={true}
						str={`Protected with ${ticketProtectionItem?.Item?.Name}`}
						variant="body2" />
				</Flex>
			);
		}
		else {
			return <>{(this.props.item.OrderableType === OrderableTypes.TicketProtectionInstance)}</>;
		}
	}


	/**
	 * Render the delivery method information.
	 *
	 * @return {ReactNode}
	 */
	renderDeliveryMethod() {
		if (this.props.item.DeliveryMethodItemUuid) {
			const deliveryMethodItem = this.props.checkoutBasket.find(i => i.Uuid === this.props.item.DeliveryMethodItemUuid);

			return (
				<String
					color="textSecondary"
					display="inline-block"
					noFlex={true}
					str={deliveryMethodItem?.Item?.Label}
					variant="body2" />
			);
		}
		else if (DeliveryMethodUtils.doesBasketItemRequireDelivery(this.props.item)) {
			return (
				<String
					color="error"
					display="inline-block"
					noFlex={true}
					str={this.props.noDeliveryMethodWarningText || "Please choose a delivery option at checkout"}
					variant="body2" />
			);
		}
		else {
			return <>{(this.props.item.OrderableType === OrderableTypes.DeliveryMethod)}</>;
		}
	}


	/**
	 * Render the discounts section.
	 * 
	 * @return {ReactNode}
	 */
	renderDiscounts() {

		const itemQty = this.props.item.Quantity;

		return this.props.discounts.map((discount, key) => {

			const {Code, Amount} = discount.Discount;
			const qty = Math.min(discount.DiscountQty, itemQty);
			const totalDiscount = Math.max((Amount * qty), 0);

			let label = `Discount: ${Code}`;

			if (qty !== itemQty) {
				label += ` (applied to ${qty} ${pluralize("item", qty)})`;
			}

			return (
				<Flex
					columnar={true}
					justifyContent="space-between"
					key={key}>
					<String
						color="error"
						noFlex={true}
						str={label}
						variant="body2" />
					<String
						className={scss.price}
						color="error"
						noFlex={true}
						str={`-${Localisation.formatCurrency(totalDiscount)}`}
						variant="body2" />
				</Flex>
			);

		});

	}


	/**
	 * Render the seat reservations button.
	 * 
	 * @return {ReactNode}
	 */
	renderSeatReservation() {
		return (
			<div>
				<Badge
					badgeContent={(this.props.pendingSeatSelection ? 1 : 0)}
					className={scss.seatReservationsBadge}
					color="error"
					variant="dot">
					<Button
						className={scss.seatReservationsBtn}
						color="secondary"
						label="Seat Reservations"
						onClick={this.handleSeatReservation}
						size="small"
						startIcon={<SeatingIcon />}
						variant="outlined" />
				</Badge>
			</div>
		);
	}

	/**
	 * Render the seat reservations.
	 * 
	 * @return {ReactNode}
	 */
	renderSeatReservationView() {

		const seatReservations = this.props.checkoutBasket?.filter(relatedItem => relatedItem.OrderableType === OrderableTypes.SeatReservation &&
		relatedItem.RelatedItemUuid === this.props.item.Uuid) ?? [];

		return (
			<div>

				{/** !!seatReservations.length &&
						<Flex
							alignItems="center"
							columnar={true}
							gap={0.5}>
							<String str="Spaces" variant="subtitle2" />
							{seatReservations?.map((seat, key) => <SeatReservationChip key={key} seat={seat} />)}
						</Flex>
				*/}

				{seatReservations.some(item => item.Item?.WasBestAllocation === false) && <String bold={true} color="warning" gap={0} str="Seat reservations for this item could not be allocated in a single block. Only continue if you are happy to be seated separately." variant="body2" />}

				{/** <Link
					endArrow={true}
					label="View Reservations"
					onClick={this.handleSeatReservation} /> */}
			</div>
		);
	}

}

export default withCheckoutBasket(withMobile(BasketCardItem));
